import type { ConciergeListItem } from '@/api/concierge';
import type { RefType, Ticket } from '@/api/support';
import type { CardActionView } from '@/api/cardActions';
import type { TopUpView } from '@/api/topup';
import { askedAmount, methodShortKey } from './topUpChat';
import { formatDisplayAmount } from '@/utils/money';
import type { TaskFilter } from '@/views/vitrine/concierge/conciergeUi';

/**
 * «Переписки» of the concierge tab and the rule for which feed the tab opens
 * (docs/cabinet-redesign/02_ЭКРАНЫ_БЫЛО_СТАЛО.md §3.5–3.6). Pure functions — the screen only renders their result.
 */

/** Threads of a live request (top-up, card issuance, a money request, a family vouch) — a group of their own. */
const REQUEST_REF_TYPES: ReadonlySet<RefType> = new Set<RefType>(['TOPUP_REQUEST', 'CARD_ISSUANCE', 'MONEY_REQUEST',
  'IDENTITY_VOUCH']);

export interface ThreadGroups {
  /** Concierge tasks, newest activity first. */
  tasks: ConciergeListItem[];
  /** Threads of live requests (top-up, card issuance, money requests). */
  byRequest: Ticket[];
  /** The general chat, past conversations and any other thread kind (CARD_CHANGE, STAFF, future ones). */
  conversations: Ticket[];
}

/** A thread is in progress while it is open or assigned; RESOLVED and CLOSED are finished (02 §3.6). */
export function ticketIsActive(t: Pick<Ticket, 'status'>): boolean {
  return t.status === 'OPEN' || t.status === 'ASSIGNED';
}

export function filterTickets<T extends Pick<Ticket, 'status'>>(items: T[], filter: TaskFilter): T[] {
  if (filter === 'all') return items;
  return items.filter(t => ticketIsActive(t) === (filter === 'active'));
}

function time(v: string | null | undefined): number {
  const n = v ? Date.parse(v) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/**
 * Splits the client's threads into the three groups. A CONCIERGE thread is never listed by itself — the task already
 * stands for it (de-duplication by ticketId / refId). Tasks are ordered by the last message of their thread when the
 * thread is known, else by their own update / creation time.
 */
export function buildThreadGroups(tasks: ConciergeListItem[], tickets: Ticket[]): ThreadGroups {
  const lastByTicket = new Map(tickets.map(t => [t.id, time(t.lastMessageAt)]));
  const lastByTask = new Map(
    tickets.filter(t => t.refType === 'CONCIERGE' && t.refId).map(t => [t.refId as string, time(t.lastMessageAt)]),
  );
  const activity = (r: ConciergeListItem) =>
    (r.ticketId ? lastByTicket.get(r.ticketId) : undefined) ?? lastByTask.get(r.id) ?? (time(r.updatedAt) || time(r.createdAt));

  const sortedTasks = [...tasks].sort((a, b) => activity(b) - activity(a));
  const others = tickets.filter(t => t.refType !== 'CONCIERGE').sort((a, b) => time(b.lastMessageAt) - time(a.lastMessageAt));
  return {
    tasks: sortedTasks,
    byRequest: others.filter(t => t.refType != null && REQUEST_REF_TYPES.has(t.refType)),
    conversations: others.filter(t => t.refType == null || !REQUEST_REF_TYPES.has(t.refType)),
  };
}

export type Lane =
  | { kind: 'task'; taskId: string }
  | { kind: 'thread'; ticketId: string }
  | { kind: 'general' };

/** The stage at which the client has something to pay (ConciergeClientStage → «К оплате»). */
const TO_PAY_STAGE = 'TO_PAY';

/**
 * Which feed the concierge tab opens with no `thread` / `new` / `threads` parameter and no draft (02 §3.5):
 * (1) the freshest thread with an unread manager message; (2) a task waiting for payment; (3) the general chat.
 * A CONCIERGE thread opens its task.
 */
export function pickLane(tasks: ConciergeListItem[], tickets: Ticket[], unreadByTicket: Record<string, number>): Lane {
  const unread = tickets
    .filter(t => (unreadByTicket[t.id] ?? 0) > 0)
    .sort((a, b) => time(b.lastMessageAt) - time(a.lastMessageAt));
  for (const t of unread) {
    if (t.refType === 'CONCIERGE') {
      const task = t.refId ?? tasks.find(r => r.ticketId === t.id)?.id;
      if (task) return { kind: 'task', taskId: task };
      continue;
    }
    return { kind: 'thread', ticketId: t.id };
  }
  const toPay = tasks.find(r => r.stage === TO_PAY_STAGE);
  if (toPay) return { kind: 'task', taskId: toPay.id };
  return { kind: 'general' };
}

/**
 * The mark on the concierge tab's clock (prototype D: the threads live in the history behind it). `unread` — a manager
 * answered in a thread that is not on screen; `open` — another conversation or request is still in progress; null —
 * nothing waits behind the clock. The thread on screen (null = the general chat) never marks it.
 */
export function threadsMarkOf(tickets: Pick<Ticket, 'id' | 'status' | 'refType'>[], unreadByTicket: Record<string, number>,
                              onScreen: string | null): 'unread' | 'open' | null {
  // The general chat on screen is the live GENERAL thread; a finished one still counts as «elsewhere».
  const elsewhere = tickets.filter(t => (onScreen ? t.id !== onScreen : !(t.refType === 'GENERAL' && ticketIsActive(t))));
  if (elsewhere.some(t => (unreadByTicket[t.id] ?? 0) > 0)) return 'unread';
  if (elsewhere.some(ticketIsActive)) return 'open';
  return null;
}

/** Where a `?thread=<id>` link leads: a CONCIERGE thread opens its task; an unknown id falls back to the general chat. */
export function resolveThreadLink(ticketId: string, tickets: Ticket[]): Lane {
  const t = tickets.find(x => x.id === ticketId);
  if (!t) return { kind: 'general' };
  if (t.refType === 'CONCIERGE' && t.refId) return { kind: 'task', taskId: t.refId };
  return { kind: 'thread', ticketId: t.id };
}

/**
 * A top-up thread's title (02 §3.6, owner 31.10): «Пополнение · 1 000 USD · банк» — what was asked and how; without
 * the request (an older one, or the list failed) «Заявка на пополнение». `t`/`date` are the screen's i18n functions.
 */
export function topUpThreadTitle(r: TopUpView | null | undefined, t: (key: string, params?: Record<string, unknown>) => string,
                                 date?: (d: Date) => string): string {
  if (!r) return t('support.thread.TOPUP_REQUEST');
  const asked = askedAmount(r);
  const method = r.sourceKind === 'CRYPTO' ? t('cabinet.topupChat.method.crypto') : t(methodShortKey(r.sourceKind));
  if (!asked) {
    return date ? `${t('cabinet.topupChat.threadTitleShort', { method })} · ${date(new Date(r.createdAt))}`
      : t('cabinet.topupChat.threadTitleShort', { method });
  }
  // a whole sum reads without its zero cents, as the owner wrote it: «Пополнение · 1 000 USD · банк»
  const amount = formatDisplayAmount(asked.amount, asked.currency).replace(/[.,]00$/, '');
  return t('cabinet.topupChat.threadTitle', { amount: `${amount} ${asked.currency}`, method });
}

/**
 * A card-action thread's title: «Заморозка карты ··1111» / «Блокировка карты ··1111» / «Разморозка карты ··1111» — so two
 * requests do not both read «Обслуживание карты». Without the request the generic title stays.
 */
export function cardActionThreadTitle(r: Pick<CardActionView, 'action' | 'cardLastFour'> | null | undefined,
                                      t: (key: string, params?: Record<string, unknown>) => string): string {
  if (!r) return t('support.thread.CARD_CHANGE');
  const what = t(`support.thread.cardAction.${r.action}`);
  return r.cardLastFour ? `${what} ··${r.cardLastFour}` : what;
}
